/**
 * Token status registry.
 *
 * A token stores only an array of status *keys* (e.g. ["poisoned","prone"]).
 * Presentation (display name + icon) is resolved here, so the same key renders
 * consistently everywhere and a status can be renamed or re-iconed in one place.
 *
 * Built-in statuses live in this static map — no DB needed. Custom,
 * campaign-defined statuses are a planned extension (a StatusDef table the
 * registry would merge over these built-ins); see NPC-6 follow-up.
 *
 * Icon art: statuses with an `iconUrl` render the PNG under
 * public/status-icons/ (normalized to 128x128 by
 * scripts/normalize-status-icons.py). Statuses without art (prone — absent
 * from the source set) keep the emoji/SVG fallback.
 */

/** Picker section a status belongs to. */
export type StatusGroup = 'condition' | 'number' | 'color' | 'symbol'

export interface StatusDef {
  key: string
  name: string
  /** Icon glyph — emoji fallback so no asset load is needed. Used when the
   *  status has no `iconUrl` art. */
  icon: string
  /** Picker section (Conditions / Numbers / Colors / Symbols). */
  group: StatusGroup
  /** Optional image icon (site-relative URL under /status-icons/). When set,
   *  renderers prefer it over the emoji/SVG glyph. */
  iconUrl?: string
}

const ICON_BASE = '/status-icons'

/** Shorthand for a built-in def whose art file is named after its key. */
function def(
  key: string,
  name: string,
  icon: string,
  group: StatusGroup,
  hasArt = true
): StatusDef {
  return hasArt
    ? { key, name, icon, group, iconUrl: `${ICON_BASE}/${key}.png` }
    : { key, name, icon, group }
}

/** Built-in statuses (system-agnostic, D&D-leaning). Keys are stable ids —
 *  the original 12 condition keys are unchanged; only presentation moved to
 *  image icons. */
export const BUILTIN_STATUSES: Record<string, StatusDef> = {
  // ── Conditions (original 12 — keys stable) ──
  poisoned: def('poisoned', 'Poisoned', '🤢', 'condition'),
  prone: def('prone', 'Prone', '🛌', 'condition', false), // no art in the set
  stunned: def('stunned', 'Stunned', '💫', 'condition'),
  blinded: def('blinded', 'Blinded', '🙈', 'condition'),
  charmed: def('charmed', 'Charmed', '💗', 'condition'),
  frightened: def('frightened', 'Frightened', '😱', 'condition'),
  grappled: def('grappled', 'Grappled', '🤝', 'condition'),
  restrained: def('restrained', 'Restrained', '⛓️', 'condition'),
  unconscious: def('unconscious', 'Unconscious', '💤', 'condition'),
  concentrating: {
    key: 'concentrating',
    name: 'Concentrating',
    icon: '🌀',
    group: 'condition',
    iconUrl: `${ICON_BASE}/concentration.png`, // art file uses the D&D term
  },
  blessed: def('blessed', 'Blessed', '✨', 'condition'),
  burning: def('burning', 'Burning', '🔥', 'condition'),
  // ── Conditions (new with the 2026-07 art drop) ──
  bane: def('bane', 'Bane', '🥀', 'condition'),
  dead: def('dead', 'Dead', '💀', 'condition'),
  deafened: def('deafened', 'Deafened', '🙉', 'condition'),
  glowing: def('glowing', 'Glowing', '🌟', 'condition'),
  hasted: def('hasted', 'Hasted', '💨', 'condition'),
  hexed: def('hexed', 'Hexed', '🧿', 'condition'),
  hidden: def('hidden', 'Hidden', '🫥', 'condition'),
  'hunters-marked': def('hunters-marked', "Hunter's Marked", '🎯', 'condition'),
  incapacitated: def('incapacitated', 'Incapacitated', '🚫', 'condition'),
  invisible: def('invisible', 'Invisible', '👻', 'condition'),
  paralyzed: def('paralyzed', 'Paralyzed', '🧊', 'condition'),
  petrified: def('petrified', 'Petrified', '🗿', 'condition'),
  shocked: def('shocked', 'Shocked', '⚡', 'condition'),
  slowed: def('slowed', 'Slowed', '🐌', 'condition'),
  // ── Number badges ──
  'number-0': def('number-0', '0', '0️⃣', 'number'),
  'number-1': def('number-1', '1', '1️⃣', 'number'),
  'number-2': def('number-2', '2', '2️⃣', 'number'),
  'number-3': def('number-3', '3', '3️⃣', 'number'),
  'number-4': def('number-4', '4', '4️⃣', 'number'),
  'number-5': def('number-5', '5', '5️⃣', 'number'),
  'number-6': def('number-6', '6', '6️⃣', 'number'),
  'number-7': def('number-7', '7', '7️⃣', 'number'),
  'number-8': def('number-8', '8', '8️⃣', 'number'),
  'number-9': def('number-9', '9', '9️⃣', 'number'),
  // ── Color dots ──
  'color-red': def('color-red', 'Red', '🔴', 'color'),
  'color-orange': def('color-orange', 'Orange', '🟠', 'color'),
  'color-yellow': def('color-yellow', 'Yellow', '🟡', 'color'),
  'color-green': def('color-green', 'Green', '🟢', 'color'),
  'color-blue': def('color-blue', 'Blue', '🔵', 'color'),
  'color-purple': def('color-purple', 'Purple', '🟣', 'color'),
  'color-pink': def('color-pink', 'Pink', '🌸', 'color'),
  'color-brown': def('color-brown', 'Brown', '🟤', 'color'),
  'color-black': def('color-black', 'Black', '⚫', 'color'),
  'color-white': def('color-white', 'White', '⚪', 'color'),
  // ── Symbols — source art export was empty (zero-byte PNGs), so these are
  //    emoji-only until real art lands; see scripts/normalize-status-icons.py ──
  'symbol-check': def('symbol-check', 'Check Mark', '✔️', 'symbol'),
  'symbol-x': def('symbol-x', 'X', '❌', 'symbol'),
  'symbol-exclamation': def('symbol-exclamation', 'Exclamation', '❗', 'symbol'),
  'symbol-question': def('symbol-question', 'Question', '❓', 'symbol'),
}

/** Ordered list of built-in statuses for rendering a picker. */
export const BUILTIN_STATUS_LIST: StatusDef[] = Object.values(BUILTIN_STATUSES)

/** Picker section order + labels. */
export const STATUS_GROUP_ORDER: Array<{ group: StatusGroup; label: string }> = [
  { group: 'condition', label: 'Conditions' },
  { group: 'number', label: 'Numbers' },
  { group: 'color', label: 'Colors' },
  { group: 'symbol', label: 'Symbols' },
]

/** Built-in statuses bucketed by group, in STATUS_GROUP_ORDER order. */
export const BUILTIN_STATUS_GROUPS: Array<{
  group: StatusGroup
  label: string
  statuses: StatusDef[]
}> = STATUS_GROUP_ORDER.map(({ group, label }) => ({
  group,
  label,
  statuses: BUILTIN_STATUS_LIST.filter((s) => s.group === group),
}))

/** Resolve a key to its definition. Unknown keys fall back to a neutral marker
 *  so a not-yet-loaded custom status still renders something rather than
 *  vanishing. */
export function resolveStatus(key: string): StatusDef {
  return BUILTIN_STATUSES[key] ?? { key, name: key, icon: '❓', group: 'condition' }
}

/**
 * Normalize a raw statuses value into a clean, de-duplicated string-key array.
 * Tolerates the Json column arriving as unknown: non-arrays → [], non-string
 * entries dropped, blanks trimmed out, duplicates collapsed (order preserved).
 * Used on both the read path (render) and the write path (validation).
 */
export function normalizeStatuses(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const entry of raw) {
    if (typeof entry !== 'string') continue
    const key = entry.trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}
